import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { chosenFilesTotalBytes, withFileFields } from '@helpers/formFileFields';
import './AlpacaForm.less';

// Alpaca internally wraps field change events in setTimeout(..., 200). This value must exceed
// that delay so all cascading change events (e.g. from dependency resolution) settle before
// we read getValue() and propagate to onChange.
const CHANGE_DEBOUNCE_MS = 300;

const AlpacaForm = forwardRef(({
    formId,
    formSchema: _formSchema,
    callback,
    updateFormJsonCb,
    onChange,
    onFormReady,
    hideValidationErrors,
    formHook = {}, // { preRender, init, postRender, onFormChange } — all optional
}, ref) => {
    const [formSchema, setFormSchema] = useState(_formSchema || {});
    const [formRendered, setFormRendered] = useState(false);
    const formRef = useRef(null);
    const alpacaTimer = useRef();
    const changeTimer = useRef();
    const silentClearTimer = useRef();
    const silentUpdateDepthRef = useRef(0);
    // Set by any input or change event in the form outside a silent setFormData, so callers can tell
    // a form someone edited from one that was only rendered.
    const touchedRef = useRef(false);
    // Captured before destroy, restored after rebuild — counters the browser
    // clamping scrollY when the body shrinks during the destroy/init gap.
    const scrollPinYRef = useRef(null);

    const restoreScrollPin = () => {
        const targetY = scrollPinYRef.current;
        if (targetY === null) return;
        scrollPinYRef.current = null;
        if (Math.abs(window.scrollY - targetY) > 0.5) {
            window.scrollTo(0, targetY);
        }
    };

    const getFormData = () => {
        const $form = getAlpacaFormElement();
        return $form.alpaca('get').getValue();
    }

    // A file field's value is the placeholder the legacy form code pushed into it, not the bytes.
    // Callers that POST the form as JSON must send this instead, or the backend receives "C:\fakepath\name.pdf".
    const getFormDataWithFiles = async () => withFileFields(getFormData(), formRef.current);

    const actions = {
        getFormData: getFormData,
        isTouched: () => touchedRef.current,
        getFormDataWithFiles: getFormDataWithFiles,
        chosenFilesTotalBytes: () => chosenFilesTotalBytes(formRef.current),
        setFormData: (data, overwrite = false, options = {}) => {
            const $form = getAlpacaFormElement();
            const existingData = $form.alpaca('get').getValue() || {};
            if (options.silent) {
                silentUpdateDepthRef.current += 1;
                if (silentClearTimer.current) {
                    clearTimeout(silentClearTimer.current);
                }
            }
            $form.alpaca('get').setValue(overwrite ? data : { ...existingData, ...data });
            if (options.silent) {
                silentClearTimer.current = setTimeout(() => {
                    silentUpdateDepthRef.current = 0;
                    silentClearTimer.current = null;
                }, CHANGE_DEBOUNCE_MS);
            }
        },
        getContainer: () => {
            const $form = getAlpacaFormElement();
            return $form.alpaca('get');
        },
        reset: () => {
            // TODO: "refresh" doesn't refresh properly as it recreates the form with different DOM,
            // "alpaca-top" is missing + certain functions don't work the same anymore such as "refreshValidationState"
            const $form = getAlpacaFormElement();
            const alpacaContainer = $form.alpaca('get');
            alpacaContainer.setValue({});
            alpacaContainer.refresh();
        },
        validate: (
            validCallback = () => { },
            invalidCallback = () => { },
        ) => {
            const $form = getAlpacaFormElement();
            const alpacaContainer = $form.alpaca('get');
            if (!alpacaContainer) {
                invalidCallback();
                return;
            }
            alpacaContainer.refreshValidationState(true, async () => {
                if (!alpacaContainer.isValid(true)) {
                    invalidCallback();
                    alpacaContainer.focus();
                    notification(window.global_i18n('Please fix the errors and try again.'), { type: 'warning' });
                    return;
                };
                validCallback();
            });
        },
        isValid: () => {
            const $form = getAlpacaFormElement();
            const alpacaContainer = $form.alpaca('get');
            if (!alpacaContainer) return false;
            alpacaContainer.validate(true);
            return alpacaContainer.isValid(true);
        },
    }
    const baseHookContext = {
        id: formId,
        rendered: formRendered,
        actions: actions,
        data: formSchema.data,
    };

    const getAlpacaFormElement = () => {
        return $(formRef.current).find('> .alpaca-field');
    };

    //Config is an array-type schema (e.g. extra questions) when it has schema.type === "array"
    const isArraySchema = (config) => {
        if (!config?.schema) return true;
        if (config.schema.type === 'array') return true;
        return !config.schema.properties || typeof config.schema.properties !== 'object';
    };

    // Text inputs and textareas in Alpaca fire the native 'change' event only on blur, so
    // keystrokes are invisible to control.on('change'). Bridge this by delegating native
    // 'input' events from the form container so every keystroke updates formData in React state.
    const attachInputListeners = ($form, control) => {
        $form.off('input.alpaca-touch change.alpaca-touch');
        $form.on('input.alpaca-touch change.alpaca-touch', () => {
            if (silentUpdateDepthRef.current === 0) touchedRef.current = true;
        });
        $form.off('input.alpaca-inputs');
        $form.on('input.alpaca-inputs', 'input[type="text"], input[type="search"], textarea', function () {
            const formData = control.getValue();
            onChange?.(formData);
            formHook?.onFormChange?.({ ...baseHookContext, form: $form, data: formData, control });
        });
    };

    useEffect(() => {
        setFormSchema(_formSchema);
    }, [_formSchema]);

    useEffect(() => {
        let cleanUp = null;
        // Init Form
        alpacaTimer.current = setTimeout(() => {
            const $form = $(formRef.current);
            // Use for special cases, where we don't want to show validation errors in form
            // it won't prevent form submission, but only hide the validation errors in the form
            // e.g. show the validation error in the notification (toast) instead of in the form
            if (hideValidationErrors) {
                $form.addClass('hide-validation-errors');
            }

            // Deep clone before handing to Alpaca — it mutates the config in place
            // (setValidator, registerView, postRender, etc.).
            // JSON round-trip strips functions; re-attach them from the original.
            const cloned = JSON.parse(JSON.stringify(formSchema));
            (function restoreFunctions(target, source) {
                for (const key in source) {
                    if (typeof source[key] === 'function') {
                        target[key] = source[key];
                    } else if (source[key] && typeof source[key] === 'object' && target[key]) {
                        restoreFunctions(target[key], source[key]);
                    }
                }
            })(cloned, formSchema);
            const sanitizedFormSchema = window?.sanitizeAlpacaDisplayData
                ? window.sanitizeAlpacaDisplayData(cloned)
                : cloned;
            const schemaPostRender = sanitizedFormSchema.postRender;

            if (isArraySchema(sanitizedFormSchema)) {
                // no alpacaForm.updateJsonConfiguration
                formHook?.preRender?.({ ...baseHookContext });
                $form.alpaca(sanitizedFormSchema);
                const $control = $form.find('> .alpaca-field');
                if ($control.length && $control.alpaca('exists')) {
                    const control = $control.alpaca('get');
                    const formHandler = new window.alpacaForm();
                    formHandler.init(null, $form);
                    cleanUp = formHook?.init?.({ ...baseHookContext, form: formHandler });
                    formHandler.defaultPostRender(control, function () {
                        if (typeof updateFormJsonCb === 'function') {
                            updateFormJsonCb();
                        }
                        restoreScrollPin();
                    });
                    control.on('change', function () {
                        const formData = control.getValue();
                        onChange?.(formData);
                        formHook?.onFormChange?.({ ...baseHookContext, form: formHandler, data: formData, control });
                    });
                    attachInputListeners($form, control);
                    if (typeof schemaPostRender === 'function') {
                        schemaPostRender(control);
                    }
                    formHook?.postRender?.({ ...baseHookContext, form: formHandler, control });
                    setFormRendered(true);
                }
                if (typeof callback === 'function') {
                    callback(formId, { formSchema: sanitizedFormSchema });
                }
                return;
            }

            let formHandler = new window.alpacaForm();
            formHandler.postRender = function () {
                return function (control) {
                    setFormRendered(true);

                    formHandler.defaultPostRender(control, function () {
                        if (typeof updateFormJsonCb === 'function') {
                            updateFormJsonCb();
                        }
                        restoreScrollPin();
                    });

                    control.on('change', function () {
                        if (silentUpdateDepthRef.current > 0) {
                            return;
                        }
                        if (changeTimer.current) clearTimeout(changeTimer.current);
                        changeTimer.current = setTimeout(() => {
                            const formData = control.getValue();
                            onChange?.(formData);
                            formHook?.onFormChange?.({ ...baseHookContext, form: formHandler, data: formData, control });
                        }, CHANGE_DEBOUNCE_MS);
                    });
                    attachInputListeners($form, control);

                    if (typeof schemaPostRender === 'function') {
                        schemaPostRender(control);
                    }
                    formHook?.postRender?.({ ...baseHookContext, form: formHandler, control });
                };
            };
            formHandler.init(null, $form);
            formHook?.preRender?.({ ...baseHookContext, form: formHandler });
            cleanUp = formHook?.init?.({ ...baseHookContext, form: formHandler });
            formHandler.updateJsonConfiguration(sanitizedFormSchema);
            if (typeof callback == "function") {
                callback(formId, { formSchema: sanitizedFormSchema })
            }
        }, 100);

        return () => {
            clearTimeout(alpacaTimer.current);
            alpacaTimer.current = null;
            // Without this, a debounced change event scheduled by control.on('change')
            // can fire after the alpaca instance is destroyed and call into a null
            // container via onChange → ref.current.isValid().
            clearTimeout(changeTimer.current);
            changeTimer.current = null;
            clearTimeout(silentClearTimer.current);
            silentClearTimer.current = null;
            silentUpdateDepthRef.current = 0;
            touchedRef.current = false;
            scrollPinYRef.current = window.scrollY;
            const $form = $(formRef.current);
            const $alpacaForm = getAlpacaFormElement();
            if ($alpacaForm.alpaca('exists')) {
                $alpacaForm.alpaca('destroy');
                $form.off('input.alpaca-inputs input.alpaca-touch change.alpaca-touch');
                $form.html('');
            }
            if (cleanUp) cleanUp();
        };
    }, [formSchema]);

    useEffect(() => {
        if (formRendered && typeof onFormReady === 'function') {
            onFormReady();
        }
    }, [formRendered]);

    useImperativeHandle(ref, () => actions);

    return <form ref={formRef} className="alpaca-form" id={formId}></form>;
});

export default AlpacaForm;
